import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Business,
  LockOutlined,
  PersonOutline,
  Shield,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { AuthContext } from './AuthContext';

const Logo = '/seabird_hero_logo.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login, loading: authLoading, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('session') === 'expired') {
      setError('Your session has expired. Please login again.');
    }
  }, [location]);

  const handleLoginSubmit = async event => {
    event.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please provide both email and password.');
      return;
    }

    setLoading(true);

    try {
      await login(email.trim(), password);
      navigate('/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Unable to sign in. Please try again.');
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword(prev => !prev);
  const handleMouseDownPassword = event => event.preventDefault();

  const isSubmitting = loading || authLoading;

  // Don't show login form if already authenticated (redirect will happen)
  if (isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#F8F9FC'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        backgroundColor: '#F8F9FC'
      }}
    >
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Fade in timeout={300}>
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid #E4E7EC',
                  borderRadius: 3,
                  p: { xs: 3, sm: 5, md: 6 },
                  boxShadow: '0 8px 24px rgba(16, 24, 40, 0.06)',
                  backgroundColor: '#FFFFFF'
                }}
              >
                <Box sx={{ mb: 4, textAlign: 'center' }}>
                  <img src={Logo} alt="Seabird Forms Logo" style={{ height: 280, width: 'auto' }} />
                </Box>

                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#101828', mb: 0.5 }}>
                    Welcome Back
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: '#667085' }}>
                    Sign in to access your dashboard
                  </Typography>
                </Box>

                {error && (
                  <Fade in>
                    <Alert severity="warning" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  </Fade>
                )}

                <Box component="form" onSubmit={handleLoginSubmit}>
                  <TextField
                    fullWidth
                    label="Email"
                    variant="outlined"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    disabled={isSubmitting}
                    required
                    autoComplete="email"
                    sx={{ mb: 2.5 }}
                    InputProps={{
                      sx: { fontSize: '1.05rem', py: 0.5 },
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutline sx={{ color: 'action.active', fontSize: 22 }} />
                        </InputAdornment>
                      )
                    }}
                    InputLabelProps={{ sx: { fontSize: '1.05rem' } }}
                  />

                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    variant="outlined"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    disabled={isSubmitting}
                    required
                    autoComplete="current-password"
                    sx={{ mb: 3 }}
                    InputProps={{
                      sx: { fontSize: '1.05rem', py: 0.5 },
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined sx={{ color: 'action.active', fontSize: 22 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                            disabled={isSubmitting}
                            size="large"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    InputLabelProps={{ sx: { fontSize: '1.05rem' } }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isSubmitting}
                    sx={{
                      py: 1.5,
                      mb: 1.5,
                      backgroundColor: '#2C63FF',
                      '&:hover': {
                        backgroundColor: '#1F4CD4'
                      },
                      textTransform: 'none',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderRadius: 2
                    }}
                  >
                    {isSubmitting ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
                  </Button>

                  {process.env.NODE_ENV === 'development' && (
                    <Button
                      type="button"
                      fullWidth
                      variant="outlined"
                      size="medium"
                      onClick={() => {
                        setEmail('admin@dailey.cloud');
                        setPassword('demo123');
                      }}
                      sx={{
                        py: 1.2,
                        textTransform: 'none',
                        borderColor: '#E4E7EC',
                        color: '#344054',
                        '&:hover': {
                          borderColor: '#D0D5DD',
                          backgroundColor: '#F8FAFC'
                        }
                      }}
                    >
                      Use demo credentials
                    </Button>
                  )}
                </Box>

                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#667085' }}>
                    Secured by
                  </Typography>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '999px',
                        backgroundColor: '#2C63FF',
                        color: '#FFFFFF',
                        fontSize: 12,
                        fontWeight: 600,
                        letterSpacing: '0.04em'
                      }}
                    >
                      DAILEY CORE
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Fade>
          </Grid>
        </Grid>

        {!isMobile && (
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: '1px solid #E4E7EC',
                  backgroundColor: '#FFFFFF'
                }}
              >
                <FeatureTile
                  icon={<Shield sx={{ color: '#2C63FF' }} fontSize="medium" />}
                  title="Centralized Security"
                  description="SSO, MFA, and audit trails backed by DAILEY CORE."
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: '1px solid #E4E7EC',
                  backgroundColor: '#FFFFFF'
                }}
              >
                <FeatureTile
                  icon={<Business sx={{ color: '#0F9488' }} fontSize="medium" />}
                  title="Tenant-Aware Access"
                  description="Role-aware routing to the right workspace."
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: '1px solid #E4E7EC',
                  backgroundColor: '#FFFFFF'
                }}
              >
                <FeatureTile
                  icon={<LockOutlined sx={{ color: '#8358FF' }} fontSize="medium" />}
                  title="Audit-Ready Signing"
                  description="Immutable timestamps and signer context."
                />
              </Paper>
            </Grid>
          </Grid>
        )}
      </Container>
    </Box>
  );
};

const FeatureTile = ({ icon, title, description }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 2,
      bgcolor: 'rgba(255, 255, 255, 0.08)',
      borderRadius: 2,
      p: 2.5
    }}
  >
    <Box sx={{ color: 'white', display: 'flex', alignItems: 'center' }}>{icon}</Box>
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.85 }}>
        {description}
      </Typography>
    </Box>
  </Box>
);

export default Login;
